"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search, SlidersHorizontal, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import PersonalView from "./PersonalView";
import PersonalChatView from "./PersonalChatView";
import CommunityMain from "./CommunityMain";
import CommunityView from "./CommunityView";
import HalperChatView from "./HalperChatView";
import instagram_analytics from "../../assets/instagram-analytics.png";
import telegram_analytics from "../../assets/telegram-analytics.png";
import facebook_analytics from "../../assets/facebook-analytics.png";
import whatsapp_analytics from "../../assets/whatsapp-analytics.png";
import mail_analytics from "../../assets/mail-analytics.png";
import { HiChevronUpDown } from "react-icons/hi2";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import {
  getHalperChatUnreadCount,
  postHalperMarkAsRead,
} from "../../api/chat/chatServices";
const API_BASE = REST_API_URL;

const platformIcons = {
  instagram: instagram_analytics,
  whatsapp: whatsapp_analytics,
  facebook: facebook_analytics,
  telegram: telegram_analytics,
  gmail: mail_analytics,
};

const getChatDisplayName = (chat, fallbackText = "Unknown user") => {
  if (!chat) return fallbackText;

  if (chat.platform === "telegram") {
    return (
      chat.full_name ||
      chat.tg_profile_name ||
      (chat.telegram_username ? `@${chat.telegram_username}` : null) ||
      (chat.tg_username ? `@${chat.tg_username}` : null) ||
      (chat.telegram_id ? `${chat.telegram_id}` : null) ||
      (chat.tg_phone ? `+${chat.tg_phone}` : null) ||
      fallbackText
    );
  }

  if (chat.platform === "whatsapp") {
    return (
      chat.wa_profile_name ||
      (chat.wa_user_id ? `+${chat.wa_user_id}` : fallbackText)
    );
  }

  return (
    chat.full_name ||
    (chat.instagram_username ? `@${chat.instagram_username}` : null) ||
    fallbackText
  );
};

const getOnOffEndpoint = (platform, chatId) => {
  const endpoints = {
    instagram: `${API_BASE}/messaging/instagram-on-off/${chatId}/`,
    whatsapp: `${API_BASE}/messaging/whatsapp-on-off/${chatId}/`,
    telegram: `${API_BASE}/messaging/telegram-on-off/${chatId}/`,
  };

  return endpoints[platform] ?? endpoints.instagram;
};

export default function Chat({ defaultHalper = false }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (defaultHalper) {
      setActiveTab("Personal");
      setSelectedChat("Halper");
      setMobileView("conversation");
    }
  }, [defaultHalper]);
  const [activeTab, setActiveTab] = useState("Personal");
  // Mobile-only single-pane toggle: "list" shows the conversation list,
  // "conversation" shows the active conversation. Ignored at md+ (two-pane).
  const [mobileView, setMobileView] = useState("list");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState([]);
  const [halperChatUnreadCount, setHalperChatUnreadCount] = useState(0);
  const [halperLastMessage, setHalperLastMessage] = useState("");
  const headers = buildHeaders();
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };
    if (showFilters) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilters]);

  const togglePlatform = (key) => {
    if (key === "all") return setFilterPlatform([]);
    setFilterPlatform((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  useEffect(() => {
    if (activeTab === "Community") loadCommunities();
  }, [activeTab]);

  useEffect(() => {
    getHalperChatUnreadCount()
      .then((data) => setHalperChatUnreadCount(data.unread_count))
      .catch(console.error);
  }, []);

  const loadCommunities = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/community/joined-communities/`, {
        headers,
      });
      const data = await res.json();

      setCommunities(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      console.error("❌ Error fetching communities:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadHalperLastMessage = async () => {
    try {
      const res = await fetch(`${API_BASE}/messaging/halper-messages-list/`, {
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.results?.[0]?.answer) {
          setHalperLastMessage(data.results[0].answer);
        }
      }
    } catch (e) {
      console.error("❌ Error fetching Halper last message:", e);
    }
  };

  const loadConversations = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/messaging/conversations/`, {
        headers,
      });
      const data = await res.json();
      setConversations(Array.isArray(data) ? data : (data?.results || []));
      loadHalperLastMessage();
    } catch (e) {
      console.error("❌ Error fetching conversations:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "Personal") loadConversations();
  }, [activeTab]);

  const toggleOnOff = async (chat) => {
    try {
      const endpoint = getOnOffEndpoint(chat.platform, chat.id);

      const res = await fetch(endpoint, {
        method: "GET",
        headers,
      });

      const data = await res.json();
      setConversations((prev) =>
        prev.map((c) =>
          c.id === chat.id ? { ...c, on_off: data.on_off ?? !chat.on_off } : c,
        ),
      );
    } catch (err) {
      console.error("❌ Error toggling on_off:", err);
    }
  };

  const filteredConversations = conversations
    .filter((chat) =>
      filterPlatform.length === 0
        ? true
        : filterPlatform.includes(chat.platform),
    )
    .filter((chat) => {
      if (!search.trim()) return true;
      const name = getChatDisplayName(chat, "").toLowerCase();
      const lastMsg = (chat.last_message || "").toLowerCase();
      const q = search.toLowerCase().trim();
      return name.includes(q) || lastMsg.includes(q);
    });

  const filteredCommunities = communities.filter(
    (community) =>
      community.name?.toLowerCase().includes(search.toLowerCase()) ||
      community.description?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex gap-5 w-full h-full bg-gray-50 p-2 rounded-2xl">
      <motion.div
        className={`${
          mobileView === "list" ? "flex" : "hidden"
        } md:flex w-full md:w-[400px] bg-white rounded-2xl flex-col p-4 gap-4 shadow-sm relative`}
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
      >
        {/* Row 1: Search & Filters */}
        <div className="flex items-center gap-3 w-full">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t("chat.searchPlaceholder", "Search")}
              className="w-full h-[46px] pl-11 pr-4 rounded-full bg-white border border-[#EAEBEF] text-gray-700 placeholder-[#9A99AB] text-[15px] focus:outline-none focus:border-[#020637] transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A99AB]"
              size={18}
            />
          </div>

          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="h-[46px] px-4 rounded-full bg-white border border-[#EAEBEF] flex items-center gap-2 text-[#475569] hover:bg-gray-50 transition cursor-pointer"
            >
              <SlidersHorizontal size={18} className="text-[#302B2B]" />
              <span className="text-[14px] font-medium text-[#71717A]">
                {t("home.filtersButton", "Filters")}
              </span>
            </button>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  className="absolute right-0 top-full mt-2 z-30 w-[240px] bg-white border border-gray-200 shadow-xl rounded-2xl p-4 flex flex-col gap-2.5"
                >
                  {[
                    { key: "all", label: t("home.platformAll"), icon: null },
                    {
                      key: "instagram",
                      label: t("home.platformInstagram"),
                      icon: instagram_analytics,
                    },
                    {
                      key: "whatsapp",
                      label: t("home.platformWhatsapp"),
                      icon: whatsapp_analytics,
                    },
                    {
                      key: "facebook",
                      label: t("home.platformFacebook"),
                      icon: facebook_analytics,
                    },
                    {
                      key: "telegram",
                      label: t("home.platformTelegram"),
                      icon: telegram_analytics,
                    },
                    {
                      key: "gmail",
                      label: t("home.platformMail"),
                      icon: mail_analytics,
                    },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex items-center justify-between w-full cursor-pointer py-1.5 px-2 hover:bg-gray-50 rounded-lg transition"
                      onClick={() => togglePlatform(item.key)}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon ? (
                          <img
                            src={item.icon}
                            className="w-5 h-5 object-contain"
                            alt={item.label}
                          />
                        ) : (
                          <HiChevronUpDown style={{ fontSize: "20px" }} />
                        )}
                        <span className="text-[#302B2B] text-[15px]">
                          {item.label}
                        </span>
                      </div>

                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center border ${
                          filterPlatform.includes(item.key) ||
                          (item.key === "all" && filterPlatform.length === 0)
                            ? "bg-[#020637] border-[#020637]"
                            : "border-[#C7C6D3]"
                        }`}
                      >
                        {(filterPlatform.includes(item.key) ||
                          (item.key === "all" &&
                            filterPlatform.length === 0)) && (
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                          >
                            <path
                              d="M1.5 4L4 6.5L8.5 1"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Row 2: Full-width Segmented Tabs */}
        <div className="w-full h-[50px] bg-[#EAEBEF] rounded-2xl p-1 flex items-center">
          <button
            type="button"
            onClick={() => {
              setActiveTab("Personal");
              setSelectedChat(null);
              setSelectedCommunity(null);
              setMobileView("list");
            }}
            className={`flex-1 h-full rounded-xl flex items-center justify-center transition-all text-[15px] ${
              activeTab === "Personal"
                ? "bg-white text-[#1E1E2D] font-bold shadow-sm"
                : "text-[#8E8E93] font-semibold hover:text-[#1E1E2D]"
            }`}
          >
            {t("home.tabPersonal", "Personal")}
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab("Community");
              setSelectedChat(null);
              setSelectedCommunity(null);
              setMobileView("list");
            }}
            className={`flex-1 h-full rounded-xl flex items-center justify-center transition-all text-[15px] ${
              activeTab === "Community"
                ? "bg-white text-[#1E1E2D] font-bold shadow-sm"
                : "text-[#8E8E93] font-semibold hover:text-[#1E1E2D]"
            }`}
          >
            {t("home.tabCommunity", "Community")}
          </button>
        </div>

        {activeTab === "Personal" && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Row 3: HALPER Chat Item */}
            <div
              onClick={() => {
                postHalperMarkAsRead().then(() => setHalperChatUnreadCount(0));
                setSelectedChat("Halper");
                setMobileView("conversation");
              }}
              className={`flex items-center justify-between w-full py-2.5 px-2 cursor-pointer transition rounded-xl ${
                selectedChat === "Halper" ? "bg-blue-50/60" : "hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative w-12 h-12 rounded-full border border-[#E5E5EB] bg-white flex items-center justify-center flex-shrink-0">
                  <span className="text-[24px] font-normal text-[#020637] select-none">
                    H
                  </span>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-bold text-[16px] text-[#1E1E2D] leading-tight">
                    {t("home.halperName", "HALPER").toUpperCase()}
                  </span>
                  <span className="text-[14px] text-[#9A99AB] truncate max-w-[250px] mt-0.5">
                    {halperLastMessage
                      ? halperLastMessage.length > 24
                        ? halperLastMessage.slice(0, 24) + "....."
                        : halperLastMessage
                      : "Do you mean you wa....."}
                  </span>
                </div>
              </div>
              {halperChatUnreadCount > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold bg-[#020637] rounded-full text-white"
                  aria-label={t("home.unreadBadgeAlt", {
                    count: halperChatUnreadCount,
                  })}
                >
                  {halperChatUnreadCount > 99
                    ? t("home.unreadBadgeMax")
                    : halperChatUnreadCount}
                </span>
              )}
            </div>

            {/* Separator line under HALPER */}
            <div className="border-b border-[#F0F1F5] w-full my-2" />

            {/* List or Empty State */}
            {loading ? (
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#020637]"></div>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-20 select-none">
                <p className="text-2xl text-[#9A99AB]/70 font-normal">
                  {t("chat.noMessagesYet", "No messages yet")}
                </p>
              </div>
            ) : (
              <div className="space-y-3 overflow-y-auto flex-1 pr-1">
                {filteredConversations.map((chat) => (
                  <motion.div
                    key={chat.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${
                      selectedChat?.id === chat.id
                        ? "border-[#020637] bg-blue-50"
                        : "border-transparent bg-gray-50 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      setSelectedChat(chat);
                      setMobileView("conversation");
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <img
                            src={
                              chat.profile_picture ||
                              chat.image_url ||
                              "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                            }
                            alt={getChatDisplayName(
                              chat,
                              t("home.unknownUser"),
                            )}
                            className="w-12 h-12 rounded-full object-cover border border-gray-200"
                          />

                          <img
                            src={platformIcons[chat.platform?.toLowerCase()]}
                            className="w-5 h-5 absolute bottom-0 right-0 rounded-full border border-white"
                            alt={chat.platform}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#302B2B] text-sm truncate">
                            {getChatDisplayName(chat, t("home.unknownUser"))}
                          </h4>
                          <p className="text-xs text-[#9A99AB] truncate">
                            {chat.last_message || t("home.noMessages")}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(
                            chat.last_message_time,
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOnOff(chat);
                          }}
                          className={`relative w-10 h-5 rounded-full transition-colors ${
                            chat.on_off ? "bg-[#020637]" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                              chat.on_off ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "Community" && (
          <div className="flex-1 overflow-y-auto">
            <h3 className="font-semibold text-[#302B2B] mb-3">
              {t("home.yourCommunitiesTitle")}
            </h3>

            {loading ? (
              <div className="flex justify-center py-5">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#020637]"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCommunities.map((community) => (
                  <motion.div
                    key={community.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 rounded-xl cursor-pointer transition-all border border-[#E5E7EB] hover:bg-gray-50"
                    onClick={() => {
                      setSelectedCommunity(community);
                      setMobileView("conversation");
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <img
                        src={community.icon}
                        className="w-12 h-12 rounded-full object-cover"
                      />

                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-[16px] text-[#302B2B]">
                            {community.name}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 text-[13px] text-[#4CAF50]">
                          <span className="text-[10px]">●</span>
                          {t("home.membersCount", {
                            count: community.joined_count?.toLocaleString(),
                          })}
                        </div>

                        <p className="text-xs text-[#9A99AB] truncate max-w-[230px]">
                          {community.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>

      <motion.div
        className={`${
          mobileView === "conversation" ? "flex" : "hidden"
        } md:flex flex-col flex-1 min-w-0 bg-white rounded-2xl shadow-sm p-2`}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <button
          type="button"
          onClick={() => setMobileView("list")}
          aria-label={t("common.back", "Back")}
          className="md:hidden flex items-center gap-1 text-[#475569] px-1 pb-1 self-start"
        >
          <ChevronLeft size={20} />
        </button>
        {activeTab === "Personal" && !selectedChat && <PersonalView />}
        {activeTab === "Personal" && selectedChat === "Halper" && (
          <HalperChatView />
        )}
        {activeTab === "Personal" &&
          selectedChat &&
          selectedChat !== "Halper" && (
            <PersonalChatView
              chat={selectedChat}
              platformIcons={platformIcons}
            />
          )}

        {activeTab === "Community" && !selectedCommunity && (
          <CommunityMain
            onSelect={(community) => {
              setSelectedCommunity(community);
              setMobileView("conversation");
            }}
            onJoined={loadCommunities}
          />
        )}
        {activeTab === "Community" && selectedCommunity && (
          <CommunityView
            community={selectedCommunity}
            onBack={() => setSelectedCommunity(null)}
          />
        )}
      </motion.div>
    </div>
  );
}
